import { Component, Element, Prop, Method, State, Event, EventEmitter, Host, h, forceUpdate } from '@stencil/core';
import { Color, ComponentProps, ComponentRef, Size } from '../../../interface';
import { camelCasetoDashed } from '../../../utils/helpers';
import { createColorClasses } from '../../../utils/theme';
import { createModal } from '../modal/modal-controller';
import { ModalComponent } from '../modal/modal-interface';
import { createNotify } from '../notify/notify-controller';
import { InputResponse } from './node-interface';

@Component({
  tag: 'lar-node',
  styleUrl: 'node.scss',
  shadow: true
})

export class Node {
  @Element() el!: HTMLElement;
  private modalOpening = false;
  private modal?: HTMLLarModalElement;
  private modalComponent?: ModalComponent;
  private moreButtonsComponent?: ModalComponent;

  /**
   * The color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;
  /**
   * Node loading or not
   */
  @Prop() loading: boolean = false;
  /**
   * Node big icon
   */
  @Prop() icon?: string;
  /**
   * Node corner small icon
   */
  @Prop({ mutable: true }) iconSmall?: string;
  /**
   * Hide node titles
   */
  @Prop() hideTitles: boolean = false;
  /**
   * Node subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Node superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Node main title
   */
  @Prop() mainTitle: string = '';
  /**
   * Should we load lar-log sub-component?
   */
  @Prop() log: boolean = false;
  /**
   * Node corner value, passed trought translation
   */
  @Prop() value?: string;
  /**
   * Value translation valriables
   */
  @Prop() valueTranslationValues?: object | string;
  /**
   * The color to use from your application's color palette for small icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * The color to use from your application's color palette for main icon
   */
  @Prop() colorIcon?: Color;
  /**
   * The color to use from your application's color palette for modal window.
   */
  @Prop() colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop() colorInputs?: Color;
   /**
    * If `true`, the user cannot interact with the node. Defaults to `false`.
    */
  @Prop() disabled = false;
  /**
   * Modal header size
   */
  @Prop() headerSizeModal?: Size = 'default';
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * The component to display inside of the modal.
   */
  @Prop() component?: ComponentRef;
  /**
   * The data to pass to the modal component.
   */
  @Prop() componentProps?: ComponentProps;
  /**
   * Larva sub-components request event
   */
  @Event() request!: EventEmitter;
  /**
   * Emitted before the modal has presented.
   */
  @Event({ eventName: 'larnodewillpresent' }) willPresent!: EventEmitter;

   /**
    * Emitted after the modal has presented.
    */
  @Event({ eventName: 'larnodedidpresent' }) didPresent!: EventEmitter;
  /**
   * Emitted before the modal has dismissed.
   */
  @Event({ eventName: 'larnodewilldismiss' }) willDismiss!: EventEmitter;

  /**
   * Emitted after the modal has dismissed.
   */
  @Event({ eventName: 'larnodediddismiss' }) didDismiss!: EventEmitter;

  @Method()
  async getModalComponent() {
    return this.modal?.getComponent();
  }

  @Method()
  async openModal() {
    if (this.modal || this.modalOpening === true){
      return;
    }
    try {
      const el = this.el.shadowRoot || this.el;
      const moveConteiner = el.querySelector('.defaultslotelements');
      const slotElements = el.querySelectorAll('.defaultslotelements > *');
      this.modalOpening = true;
      this.modal = await createModal({
        ...this.getModalProps(),
        onLarmodalwillpresent: () => { this.willPresent.emit(); },
        onLarmodaldidpresent: () => { this.didPresent.emit(); },
        onLarmodalwilldismiss: () => { this.willDismiss.emit(); },
        onLarmodaldiddismiss: () => {
          this.didDismiss.emit();
          this.modal = undefined;
          this.modalComponent = undefined;
          this.moreButtonsComponent = undefined;
          // move slot nodes back to DOM
          slotElements.forEach(e => moveConteiner.appendChild(e));
        },
        component: this.component,
        componentProps: this.componentProps
      });
      // present with slot nodes
      if (slotElements.length > 0) {
        await this.modal.attachComponent('lar-node-linked-components');
        for (const elem of Array.from(slotElements)) {
          await this.modal.attachComponent(elem);
        }
      }
      // attach additional Log Button component
      await this.modal.attachComponent('lar-node-more-buttons', {
        colorInputs: this.colorInputs || this.color,
        colorModal: this.colorModal || this.color,
        log: this.log,
        onRequest: (event: CustomEvent) => this.request.emit(event.detail), // pass request event
      });
      await this.modal.present();
      this.modalComponent = await this.modal.getComponent();
      this.moreButtonsComponent = (await this.modal.getComponents()).find(e => e.component === 'lar-node-more-buttons') || undefined;
    } catch (err) {
      console.error(err);
    } finally {
      this.modalOpening = false;
    }
  }
  /**
   * Display Larva Error
   */
  @Method()
  async error(data: any, timeout: number = 5000) {
    let msg = '';
    if (typeof data === 'string') {
      msg = data;
    } else if (data && data.message && typeof data.message === 'string') {
      msg = data.message;
    } else {
      msg = 'UNKNOWN_ERROR';
    }
    const translate = <lar-translate t={`error_messages.${msg}`} fallback={msg} />;
    const notify = await createNotify({ message: translate, color: 'danger', autoHide: timeout });
    await notify.present();
  }

  /**
   * Request response method
   */
  @Method()
  public async response(data: InputResponse) {
    switch(data.topic) {
      case 'iot-2/evt/getNodeLogs/fmt/json':
        if (this.moreButtonsComponent) {
          (this.moreButtonsComponent.element as HTMLLarNodeMoreButtonsElement)
            .response(data);
        }
        break;
    }
  }

  /**
   * Request response method
   */
  @Method()
  public async closeModal() {
    await this.modal?.dismiss();
  }

  public async disconnectedCallback() {
    const el = this.el.shadowRoot || this.el;
    const moveConteiner = el.querySelector('.defaultslotelements')
    moveConteiner.innerHTML = ``;
    return this.closeModal();
  }

  public componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    // move shadow slot elements to container so its possible to move back and forward those to modal
    const slotElements = (el.querySelector('.defaultslot slot') as HTMLSlotElement).assignedElements({ flatten: true });
    const moveConteiner = el.querySelector('.defaultslotelements');
    slotElements.forEach(e => moveConteiner.appendChild(e));
  }

  private getModalProps() {
    return {
      value: this.value || '',
      valueTranslationValues: this.valueTranslationValues,
      supTitle: this.supTitle,
      mainTitle: this.mainTitle,
      subTitle: this.subTitle,
      iconSmall: this.loading? 'loading' : this.iconSmall,
      colorHeaderInputs: this.colorInputs||this.color,
      colorIconSmall: this.colorIconSmall || '',
      headerSize: this.headerSizeModal,
      icon: this.icon,
      color: this.colorModal || this.color,
    };
  }

  private async handleNodeClick(event: MouseEvent) {
    // Don't open modal if clicking on quick actions (titles slot)
    const target = event.target as HTMLElement;
    if (target.closest('.titles-slot')) {
      return;
    }
    return this.openModal();
  }

  render() {
    let icon: HTMLLarIconElement;
    let iconSmall: HTMLLarIconElement | undefined;
    let value: HTMLLarTranslateElement;
    let hasValue = false;
    let hasIconSmall = false;

    if (this.icon && this.icon !== '') {
      icon = <lar-icon color={this.colorIcon} icon={this.icon} />;
    }

    if ((this.iconSmall && this.iconSmall !== '') || this.loading) {
      iconSmall = <lar-icon color={this.colorIconSmall || ''} icon={this.loading?'loading':this.iconSmall} />;
      hasIconSmall = true;
    }

    if (this.value && this.value !== '') {
      value = <lar-translate t={this.value} values={this.valueTranslationValues} />;
      hasValue = true;
    }
    // re-render modal and subcomponent props
    if (this.modal) {
      const modalProps = this.getModalProps();
      for (const prop of Object.keys(modalProps)) {
        const val = modalProps[prop] === undefined ? '' : modalProps[prop];
        this.modal.setAttribute(camelCasetoDashed(prop), val && typeof val === 'object' ? JSON.stringify(val) : val);
      }
    }
    // re-render sub component props
    if (this.modalComponent) {
      for (const prop of Object.keys(this.componentProps || {})) {
        const val = this.componentProps[prop] === undefined ? '' : this.componentProps[prop];
        this.modalComponent.element.setAttribute(camelCasetoDashed(prop), val && typeof val === 'object' ? JSON.stringify(val) : val);
      }
    }

    return (
      <Host
        class={{
          ...createColorClasses(this.color),
          'lar-node-disabled': this.disabled,
          'lar-node-loading': this.loading,
          'lar-node-small': this.nodeSize === 'small'
        }}
      >
        <div onClick={this.handleNodeClick.bind(this)} class="lar-node-box">
          {this.nodeSize !== 'small' && (
            <div class="lar-node-content">
              {icon && <div class="lar-icon-main">{icon}</div>}
              {this.hideTitles === false && <div class="lar-node-titles-wrapper"><lar-node-titles supTitle={this.supTitle} mainTitle={this.mainTitle} subTitle={this.subTitle} /></div>}
              <div class="lar-node-right">
                <div class="titles-slot" onClick={(e) => e.stopPropagation()}><slot name="titles" /></div>
              </div>
              {(hasValue || hasIconSmall) && (
                <div class="lar-value-badge">
                  {hasIconSmall && <div class="lar-icon-small">{iconSmall}</div>}
                  {hasValue && <div class="lar-value">{value}</div>}
                </div>
              )}
            </div>
          )}
          {this.nodeSize === 'small' && (
            <div class="lar-node-middle">
              <slot name="middle">
                <div class="lar-icon-big">{icon}</div>
                <h3><lar-translate t={this.mainTitle} /></h3>
                {(hasValue || hasIconSmall) && (
                  <div class="lar-value-badge">
                    {hasIconSmall && <div class="lar-icon-small">{iconSmall}</div>}
                    {hasValue && <div class="lar-value">{value}</div>}
                  </div>
                )}
              </slot>
            </div>
          )}
        </div>
        <div class="defaultslot"><slot /></div>
        <div class="defaultslotelements"></div>
      </Host>
    );
  }
}